import React, { useState, useEffect } from 'react';
import { FiCheck, FiCircle, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { getProgression, ProgressionResponse, TrackDetail, GradeBreakdown } from '../../config/api';
import { useLoading } from '../../context/LoadingContext';

interface ProgressionProps {
  userId: string;
  isActive?: boolean;
  preloadedData?: ProgressionResponse | null;
}

const Progression: React.FC<ProgressionProps> = ({
  userId,
  isActive = true,
  preloadedData = null
}) => {
  const [data, setData] = useState<ProgressionResponse | null>(preloadedData);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [xpPerDay, setXpPerDay] = useState(preloadedData?.dailyXpGoal ?? 10);
  const { registerLoadingComponent, setComponentLoading } = useLoading();

  useEffect(() => {
    if (preloadedData) {
      setData(preloadedData);
      setXpPerDay(preloadedData.dailyXpGoal);
      return;
    }

    const componentId = 'progression';
    registerLoadingComponent(componentId);

    if (!isActive || !userId) {
      setComponentLoading(componentId, false);
      return;
    }

    const fetchData = async () => {
      try {
        const progressionData = await getProgression(userId);
        setData(progressionData);
        setXpPerDay(progressionData.dailyXpGoal);
      } catch (err) {
        setError('Failed to load progression data');
        console.error('Error fetching progression data:', err);
      } finally {
        setComponentLoading(componentId, false);
      }
    };

    fetchData();
  }, [userId, isActive, registerLoadingComponent, setComponentLoading, preloadedData]);

  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="bg-white rounded-xl shadow-md border border-gray-100 p-6">
          <div className="text-red-500 text-center text-sm">
            {error}
          </div>
        </div>
      </motion.div>
    );
  }

  if (!data) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="bg-white rounded-xl shadow-md border border-gray-100 p-4">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-3"></div>
            <div className="h-8 bg-gray-200 rounded w-full mb-3"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </motion.div>
    );
  }

  const { currentGradeName, currentGradeXp, todayXp, dailyXpGoal, gradeBreakdown, currentGradeTracks, currentGrade } = data;
  const daysToComplete = xpPerDay > 0 ? Math.ceil(currentGradeXp.remaining / xpPerDay) : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
        {/* Collapsed View - Always visible */}
        <div className="p-4">
          {/* Today's XP */}
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-gray-600">Today's XP</span>
            <span className={`text-lg font-bold ${todayXp >= dailyXpGoal ? 'text-green-600' : 'text-blue-600'}`}>
              {todayXp.toFixed(1)}/{dailyXpGoal} XP
            </span>
          </div>

          {/* Current Grade Progress */}
          <div className="mb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-gray-700">{currentGradeName}</span>
              <span className="text-sm text-gray-500">{currentGradeXp.remaining} mastery XP remaining</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${(currentGradeXp.earned / currentGradeXp.total) * 100}%` }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
              />
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-xs text-gray-400">{currentGradeXp.earned} XP</span>
              <span className="text-xs text-gray-400">{currentGradeXp.total} XP</span>
            </div>
          </div>

          {/* XP per day slider */}
          <div className="bg-gray-50 rounded-lg p-3 mb-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500">Daily XP Goal</span>
              <span className="text-xs font-medium text-gray-700">{xpPerDay} XP/day</span>
            </div>
            <input
              type="range"
              min="5"
              max="50"
              step="5"
              value={xpPerDay}
              onChange={(e) => setXpPerDay(Number(e.target.value))}
              className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
            />
            <div className="text-center mt-2">
              <span className="text-sm font-semibold text-purple-600">
                {daysToComplete > 0 ? `Complete in ${daysToComplete} days` : 'Grade complete!'}
              </span>
            </div>
          </div>

          {/* Expand/Collapse Button */}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full flex items-center justify-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors py-1"
          >
            {isExpanded ? (
              <>
                <FiChevronUp size={14} />
                Hide course overview
              </>
            ) : (
              <>
                <FiChevronDown size={14} />
                View course overview
              </>
            )}
          </button>
        </div>

        {/* Expanded View */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="border-t border-gray-100 p-4">
                <h4 className="text-xs font-medium text-gray-500 mb-3">Course Overview</h4>
                <div className="space-y-2">
                  {gradeBreakdown.map((grade) => (
                    <GradeItem
                      key={grade.grade}
                      grade={grade}
                      isCurrent={grade.grade === currentGrade}
                      tracks={grade.grade === currentGrade ? currentGradeTracks : []}
                    />
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

// Grade item with nested tracks
const GradeItem: React.FC<{
  grade: GradeBreakdown;
  isCurrent: boolean;
  tracks: TrackDetail[];
}> = ({ grade, isCurrent, tracks }) => {
  const [showTracks, setShowTracks] = useState(isCurrent);

  const getStatusIcon = () => {
    if (grade.isComplete) {
      return <FiCheck className="text-green-500" size={14} />;
    }
    if (isCurrent) {
      return <div className="w-3 h-3 rounded-full bg-purple-500" />;
    }
    return <FiCircle className="text-gray-300" size={12} />;
  };

  const getStatusColor = () => {
    if (grade.isComplete) return 'text-green-600';
    if (isCurrent) return 'text-blue-600 font-medium';
    return 'text-gray-500';
  };

  return (
    <div>
      <div
        className={`flex items-center justify-between py-1.5 px-2 rounded ${
          isCurrent ? 'bg-blue-50' : ''
        } ${tracks.length > 0 ? 'cursor-pointer hover:bg-gray-50' : ''}`}
        onClick={() => tracks.length > 0 && setShowTracks(!showTracks)}
      >
        <div className="flex items-center gap-2">
          {getStatusIcon()}
          <span className={`text-sm ${getStatusColor()}`}>{grade.name}</span>
          {tracks.length > 0 && (
            <span className="text-gray-400">
              {showTracks ? <FiChevronUp size={12} /> : <FiChevronDown size={12} />}
            </span>
          )}
        </div>
        <span className="text-xs text-gray-400">{grade.xp} XP</span>
      </div>

      {/* Nested tracks */}
      <AnimatePresence>
        {showTracks && tracks.length > 0 && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="ml-6 mt-1 space-y-1 border-l-2 border-gray-100 pl-3">
              {tracks.map((track) => (
                <TrackItem key={track.trackId} track={track} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Track item sub-component
const TrackItem: React.FC<{ track: TrackDetail }> = ({ track }) => {
  const { name, isComplete, isCurrent } = track;

  const getStatusIcon = () => {
    if (isComplete) {
      return <FiCheck className="text-green-500" size={10} />;
    }
    if (isCurrent) {
      return <div className="w-2.5 h-2.5 rounded-full bg-purple-500" />;
    }
    return <FiCircle className="text-gray-300" size={8} />;
  };

  const getStatusColor = () => {
    if (isComplete) return 'text-green-600';
    if (isCurrent) return 'text-blue-600';
    return 'text-gray-500';
  };

  return (
    <div className={`flex items-center text-xs py-0.5 ${isCurrent ? 'font-medium' : ''}`}>
      <div className="flex items-center gap-1.5">
        {getStatusIcon()}
        <span className={getStatusColor()}>{name}</span>
      </div>
    </div>
  );
};

export default Progression;
